<template>
  <div class="pt-1 pb-2 pb-md-3">
    <transition
      mode="out-in"
      @before-enter="beforeEnter"
      @enter="enter"
      @leave="leave"
    >
      <div v-if="!customer" key="loading" class="container container--padding">
        <div class="d-none d-md-flex justify-end">
          <p class="text-small mb-2 mb-md-3">
            <span
              class="skeleton skeleton--bar d-inline-block"
              style="width: 100px"
              >&nbsp;</span
            >
          </p>
        </div>
        <div class="text-center text-md-left d-md-flex align-center">
          <h1 class="text-color-secondary-2 lh-1 mb-0 mt-2 mt-md-0 flex-grow-1">
            <span
              class="skeleton skeleton--bar d-inline-block"
              style="width: 235px"
              >&nbsp;</span
            >

            <span
              class="skeleton skeleton--bar d-inline-block"
              style="width: 300px"
              >&nbsp;</span
            >
          </h1>

          <div
            class="pt-1-5 pt-md-0 pl-md-2 d-flex flex-direction-column align-center align-md-end"
          >
            <p class="lh-1 mb-1 skeleton skeleton--bar" style="width: 200px">
              &nbsp;
            </p>
            <p
              class="h3 lh-1 mb-1 text-color-secondary-2 skeleton skeleton--bar"
              style="width: 480px"
            >
              &nbsp;
            </p>
            <p class="lh-1 mb-0 skeleton skeleton--bar" style="width: 300px">
              &nbsp;
            </p>
          </div>
        </div>

        <div class="account__menu pt-2 pt-md-4">
          <account-section-link
            v-for="i in 6"
            :key="i"
            pre-title=""
            title=""
            description=""
            to=""
            skeleton
          />
        </div>
      </div>
      <div v-else key="content" class="container container--padding">
        <div class="d-none d-md-flex justify-end">
          <p class="text-small mb-2 mb-md-3">
            <button-link with-icon icon="exit" @click="logout">
              <span>{{ $t('action.logout') }}</span>
            </button-link>
          </p>
        </div>
        <div class="text-center text-md-left d-md-flex align-center">
          <h1
            class="h2 text-color-secondary lh-1 mb-0 mt-2 mt-md-0 flex-grow-1"
            data-hello
          >
            {{ $t('account.hello') }}

            <span
              v-if="!customer"
              class="skeleton skeleton--bar"
              style="width: 100px"
            ></span>
            <span v-else-if="customer.firstName">{{ customer.firstName }}</span>
            <span v-else-if="customer.displayName">{{
              customer.displayName
            }}</span>
          </h1>

          <div
            class="pt-1-5 pt-md-0 pl-md-3 d-flex flex-direction-column align-center align-md-end text-md-right"
            data-welcome
          >
            <p class="h4 lh-1 mb-1" v-html="$t('account.welcome')"></p>
            <p class="lh-1 mb-0">
              <i class="text-italic">{{
                $t(`account.enjoy.${welcomeMessage}`, {
                  name: customer.firstName
                    ? customer.firstName
                    : customer.displayName,
                })
              }}</i>
            </p>
          </div>
        </div>

        <div class="account__menu pt-2 pt-md-4">
          <account-section-link
            :pre-title="$t('account.menu.orders.preTitle')"
            :title="$t('account.menu.orders.title')"
            :description="$t('account.menu.orders.description')"
            :to="$contextPath('/account/orders')"
            icon="commandes"
            data-card
          />
          <account-section-link
            :pre-title="$t('account.menu.profile.preTitle')"
            :title="$t('account.menu.profile.title')"
            :description="$t('account.menu.profile.description')"
            :to="$contextPath('/account/profile')"
            icon="compte"
            data-card
          />
          <account-section-link
            :pre-title="$t('account.menu.address.preTitle')"
            :title="$t('account.menu.address.title')"
            :description="$t('account.menu.address.description')"
            :to="$contextPath('/account/addresses')"
            icon="adresses"
            data-card
          />
          <account-section-link
            :pre-title="$t('account.menu.alerts.preTitle')"
            :title="$t('account.menu.alerts.title')"
            :description="$t('account.menu.alerts.description')"
            :to="$contextPath('/account/alerts')"
            icon="alertes"
            data-card
          />
          <account-section-link
            v-if="customer.numberOfOrders > 0"
            :pre-title="$t('account.menu.parrainage.preTitle')"
            :title="$t('account.menu.parrainage.title')"
            :description="$t('account.menu.parrainage.description')"
            :to="$contextPath('/account/parrainage')"
            icon="cagnotte"
            data-card
          />

          <account-section-link
            v-if="customer.numberOfOrders > 0"
            :pre-title="$t('account.menu.care_guides.preTitle')"
            :title="$t('account.menu.care_guides.title')"
            :description="$t('account.menu.care_guides.description')"
            :to="$contextPath('/blogs/guides-dentretien')"
            icon="care-guide"
            data-card
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getHours } from 'date-fns'

import accountSectionLink from '@/components/account/SectionLink'
import ButtonLink from '@/components/buttons/Link'

import { EVENTS } from '@/const/events'

export default {
  name: 'AccountIndex',
  components: {
    accountSectionLink,
    ButtonLink,
  },

  head() {
    return {
      title: `${this.$t('account.label')} - Asphalte`,
    }
  },

  computed: {
    ...mapGetters({
      customer: 'auth/customer',
    }),
    welcomeMessage() {
      if (this.customer) {
        if (this.customer.isBirthday) {
          return 'birthday'
        }

        // TODO ANNIV DATE PREMIERE COMMANDE
      }

      const today = new Date()
      const hour = getHours(today)

      if (hour > 6 && hour < 11) {
        return 'morning'
      }

      if (hour >= 17 && hour < 22) {
        return 'evening'
      }

      const r = Math.random() * 10

      if (hour >= 11 && hour < 17 && r > 5) {
        return 'day'
      }

      return 'default'
    },
  },
  methods: {
    ...mapActions({
      disconnect: 'auth/disconnectCustomer',
    }),
    async beforeEnter(el) {
      el.style.opacity = 0

      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      const hello = el.querySelector('[data-hello]')
      const welcome = el.querySelector('[data-welcome]')
      const cards = el.querySelectorAll('[data-card]')

      gsap.set(hello, { opacity: 0, x: -100 })
      gsap.set(welcome, { opacity: 0, x: 100 })
      cards.forEach((card) => {
        gsap.set(card, { opacity: 0, x: 100 })
      })
    },
    async enter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      const hello = el.querySelector('[data-hello]')
      const welcome = el.querySelector('[data-welcome]')
      const cards = el.querySelectorAll('[data-card]')

      const tl = gsap.timeline({
        onStart: () => {
          gsap.set(el, { opacity: 1 })
          cards.forEach((card) => {
            card.classList.add('no-animate')
          })
        },
        onComplete: () => {
          cards.forEach((card) => {
            card.classList.remove('no-animate')
          })
          done()
        },
      })

      tl.to(hello, {
        opacity: 1,
        x: 0,
        duration: 0.5,
        ease: 'back.out(1.7)',
      })
        .to(
          welcome,
          {
            opacity: 1,
            x: 0,
            duration: 0.5,
            ease: 'back.out(1.7)',
          },
          0
        )
        .to(
          cards,
          {
            opacity: 1,
            x: 0,
            duration: 0.5,
            stagger: 0.05,
            ease: 'back.out(1.7)',
          },
          0.25
        )

      tl.play()
    },
    async leave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.to(el, { opacity: 0, duration: 0.5, onComplete: done })
    },
    logout() {
      this.disconnect().then(() => {
        this.$logError.removeIdentify()
        this.$nuxt.$emit(EVENTS.USER_LOGGED_OUT)
        this.$router.push(this.$contextPath('/'))
      })
    },
  },
}
</script>

<style lang="scss">
.account__menu {
  display: grid;
  grid-gap: calc(var(--spacing));
  grid-template-columns: 1fr;
  grid-template-rows: auto;

  @include mq($from: tablet) {
    grid-template-columns: repeat(3, minmax(20rem, 1fr));
    grid-gap: calc(var(--spacing) * 2);
  }
}

.account__products {
  border-top: 1px solid var(--secondary-color-3);

  .slices {
    .slice:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
